// src/pages/healthcare/results/testOrderComponents/TestBasicInfo.tsx

import React from "react";
import { Input, Select, Row, Col, Typography } from "antd";
import { ExperimentOutlined } from "@ant-design/icons";

import SectionCard from "../../../../components/shared/SectionCard";
import EmptyState from "../../../../components/shared/EmptyState";
import StatusTag from "../../../../components/shared/StatusTag";

import { Test, CustomTestPackage } from "../../../../types/healthcare/TestTypes";
import { sampleTypeOptions } from "../../../../types/lab/SampleTypes";

const { TextArea } = Input;
const { Text } = Typography;

interface TestBasicInfoProps {
	currentOrder: Partial<Test>;
	selectedPackage?: CustomTestPackage;
	isNotLocked: boolean;
	handleFieldChange: (field: string, value: any) => void;
}

const TestBasicInfo: React.FC<TestBasicInfoProps> = ({ currentOrder, selectedPackage, isNotLocked, handleFieldChange }) => {
	if (!selectedPackage) {
		return (
			<EmptyState
				icon={<ExperimentOutlined style={{ fontSize: 48, color: "var(--mg-text-disabled)" }} />}
				title="Select a test package"
				description="Choose a test package to display the relevant fields"
			/>
		);
	}

	return (
		<SectionCard>
			{/* Package header */}
			<div style={{ marginBottom: 8, display: "flex", alignItems: "center", gap: 10 }}>
				<StatusTag status="info" label={selectedPackage.name} />
				{selectedPackage.description && (
					<Text type="secondary" style={{ fontSize: 11 }}>{selectedPackage.description}</Text>
				)}
			</div>

			<Row gutter={[12, 8]}>
				<Col xs={24} md={12}>
					<div>
						<Text type="secondary" style={{ display: "block", fontSize: 11, marginBottom: 4 }}>Facility</Text>
						<Input
							size="small"
							value={currentOrder.facility || ""}
							onChange={(e) => handleFieldChange("facility", e.target.value)}
							disabled={!isNotLocked}
						/>
					</div>
				</Col>

				<Col xs={24} md={12}>
					<div>
						<Text type="secondary" style={{ display: "block", fontSize: 11, marginBottom: 4 }}>Sample ID</Text>
						<Input
							size="small"
							value={currentOrder.sample_id || ""}
							onChange={(e) => handleFieldChange("sample_id", e.target.value)}
							disabled={!isNotLocked}
						/>
					</div>
				</Col>

				<Col xs={24} md={12}>
					<div>
						<Text type="secondary" style={{ display: "block", fontSize: 11, marginBottom: 4 }}>Ordering provider</Text>
						<Input
							size="small"
							value={
								currentOrder.ordering_provider
									? `${currentOrder.ordering_provider.first_name} ${currentOrder.ordering_provider.last_name}`
									: ""
							}
							disabled
						/>
					</div>
				</Col>

				<Col xs={24} md={12}>
					<div>
						<Text type="secondary" style={{ display: "block", fontSize: 11, marginBottom: 4 }}>Billable diagnosis (ICD-10)</Text>
						<Input
							size="small"
							value={currentOrder.billable_diagnosis || ""}
							onChange={(e) => handleFieldChange("billable_diagnosis", e.target.value)}
							disabled={!isNotLocked}
						/>
					</div>
				</Col>

				<Col xs={24} md={12}>
					<div>
						<Text type="secondary" style={{ display: "block", fontSize: 11, marginBottom: 4 }}>
							Sample type <span style={{ color: "var(--mg-danger)" }}>*</span>
						</Text>
						<Select
							size="small"
							value={currentOrder.sample_type || undefined}
							options={sampleTypeOptions}
							onChange={(value) => handleFieldChange("sample_type", value)}
							style={{ width: "100%" }}
							disabled={!isNotLocked}
							placeholder="Select sample type"
						/>
					</div>
				</Col>

				<Col xs={24}>
					<div>
						<Text type="secondary" style={{ display: "block", fontSize: 11, marginBottom: 4 }}>Reason for test</Text>
						<TextArea
							value={currentOrder.reason || ""}
							onChange={(e) => handleFieldChange("reason", e.target.value)}
							rows={2}
							disabled={!isNotLocked}
						/>
					</div>
				</Col>
			</Row>
		</SectionCard>
	);
};

export default TestBasicInfo;
